import { ConfirmProvider, Spinner, ToastProvider } from '@cartly/ui';
import { CheckCircle2, Info, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router';
import { ALL_NAV_ITEMS, CURRENT_PHASE } from './app/nav';
import { SessionProvider, useSession } from './app/session';
import { ThemeProvider } from './app/theme';
import { AppShell } from './layouts/AppShell';
import { SuperAdminShell } from './pages/superadmin/SuperAdminShell';
import { SuperAdminOverviewPage } from './pages/superadmin/OverviewPage';
import { LoginPage } from './pages/auth/Login';
import { MfaChallengePage } from './pages/auth/MfaChallenge';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/auth/PasswordReset';
import { RegisterPage } from './pages/auth/Register';
import { AcceptInvitePage, VerifyEmailPage } from './pages/auth/TokenPages';
import { ModulePage } from './pages/ModulePage';
import { ConnectShopifyPage } from './pages/ConnectShopify';
import { OnboardingPage } from './pages/Onboarding';
import { OverviewPage } from './pages/Overview';
import { CustomersPage } from './pages/customers/CustomersPage';
import { SegmentsPage } from './pages/customers/SegmentsPage';
import { OrdersPage } from './pages/orders/OrdersPage';
import { AutomationsPage } from './pages/automations/AutomationsPage';
import { FlowBuilderPage } from './pages/automations/FlowBuilderPage';
import { ReceiptPage } from './pages/billing/ReceiptPage';
import { BillingPage } from './pages/billing/BillingPage';
import { CatalogPage } from './pages/whatsapp/CatalogPage';
import { AlertsPage } from './pages/campaigns/AlertsPage';
import { CampaignsPage } from './pages/campaigns/CampaignsPage';
import { WhatsappPage } from './pages/whatsapp/WhatsappPage';
import { InboxPage } from './pages/inbox/InboxPage';
import { InstagramPage } from './pages/instagram/InstagramPage';
import { AiAgentsPage } from './pages/ai/AiAgentsPage';
import { AiCallingPage } from './pages/calls/AiCallingPage';
import { StoreDetailPage } from './pages/stores/StoreDetailPage';
import { StoresPage } from './pages/stores/StoresPage';
import { WebhooksSettings } from './pages/settings/Webhooks';
import { AiProviderSettings } from './pages/settings/AiProvider';
import { AuditLogSettings } from './pages/settings/AuditLog';
import { MembersSettings } from './pages/settings/Members';
import { OrganizationSettings } from './pages/settings/Organization';
import { RolesSettings } from './pages/settings/Roles';
import { PrivacySettings } from './pages/settings/Privacy';
import { SecuritySettings } from './pages/settings/Security';
import { defaultSettingsTab, SettingsLayout } from './pages/settings/SettingsLayout';

function FullPageSpinner() {
  return (
    <div className="page-center">
      <Spinner size="lg" label="Loading" />
    </div>
  );
}

/** Signed-out pages: redirect away once the user is signed in. */
function GuestOnly() {
  const { state } = useSession();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  if (state.status === 'loading') return <FullPageSpinner />;
  if (state.status === 'mfa_pending') return <Navigate to="/mfa" replace state={{ from }} />;
  if (state.status === 'active') return <Navigate to={from ?? '/'} replace />;
  return <Outlet />;
}

function MfaOnly() {
  const { state } = useSession();
  if (state.status === 'loading') return <FullPageSpinner />;
  if (state.status === 'anonymous') return <Navigate to="/login" replace />;
  if (state.status === 'active') return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Requires a fully authenticated session; optionally an active organization. */
function RequireAuth({ tenant }: { tenant: boolean }) {
  const { state } = useSession();
  const location = useLocation();
  if (state.status === 'loading') return <FullPageSpinner />;
  if (state.status === 'anonymous') {
    // Keep the query string: invite and Shopify link codes travel in it.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (state.status === 'mfa_pending') {
    return <Navigate to="/mfa" replace state={{ from: location.pathname + location.search }} />;
  }
  if (tenant && !state.session.activeTenant) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

function SettingsIndex() {
  const { can } = useSession();
  return <Navigate to={defaultSettingsTab(can)} replace />;
}

function Public({ children }: { children: ReactNode }) {
  const { state } = useSession();
  return state.status === 'loading' ? <FullPageSpinner /> : children;
}

const moduleRoutes = ALL_NAV_ITEMS.filter((i) => i.phase > CURRENT_PHASE && !i.live).map(
  (item) => ({
    path: item.path.slice(1),
    element: <ModulePage item={item} />,
  }),
);

const router = createBrowserRouter([
  {
    element: <GuestOnly />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },
  { element: <MfaOnly />, children: [{ path: '/mfa', element: <MfaChallengePage /> }] },
  // Token links work whether or not the user is signed in.
  { path: '/reset-password', element: <ResetPasswordPage /> },
  {
    path: '/verify-email',
    element: (
      <Public>
        <VerifyEmailPage />
      </Public>
    ),
  },
  {
    path: '/accept-invite',
    element: (
      <Public>
        <AcceptInvitePage />
      </Public>
    ),
  },
  {
    element: <RequireAuth tenant={false} />,
    children: [
      { path: '/onboarding', element: <OnboardingPage /> },
      { path: '/connect/shopify', element: <ConnectShopifyPage /> },
    ],
  },
  {
    // Platform administration: needs a session but not an organization. The shell hides it
    // from non-administrators; every API call behind it is authorized again on the server.
    element: <RequireAuth tenant={false} />,
    children: [
      {
        path: '/super-admin',
        element: <SuperAdminShell />,
        children: [{ index: true, element: <SuperAdminOverviewPage /> }],
      },
    ],
  },
  {
    element: <RequireAuth tenant />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <OverviewPage /> },
          { path: 'stores', element: <StoresPage /> },
          { path: 'stores/:id', element: <StoreDetailPage /> },
          { path: 'customers', element: <CustomersPage /> },
          { path: 'customers/segments', element: <SegmentsPage /> },
          { path: 'orders', element: <OrdersPage /> },
          { path: 'whatsapp', element: <WhatsappPage view="accounts" /> },
          { path: 'whatsapp/templates', element: <WhatsappPage view="templates" /> },
          { path: 'whatsapp/messages', element: <WhatsappPage view="messages" /> },
          { path: 'whatsapp/campaigns', element: <CampaignsPage /> },
          { path: 'whatsapp/alerts', element: <AlertsPage /> },
          { path: 'whatsapp/shopping', element: <CatalogPage /> },
          { path: 'inbox', element: <InboxPage /> },
          { path: 'instagram', element: <InstagramPage /> },
          { path: 'ai-agents', element: <AiAgentsPage /> },
          { path: 'ai-calling', element: <AiCallingPage /> },
          { path: 'billing', element: <BillingPage /> },
          { path: 'billing/receipt/:id', element: <ReceiptPage /> },
          { path: 'campaigns', element: <Navigate to="/whatsapp/campaigns" replace /> },
          { path: 'automations', element: <AutomationsPage /> },
          { path: 'automations/:id', element: <FlowBuilderPage /> },
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <SettingsIndex /> },
              { path: 'organization', element: <OrganizationSettings /> },
              { path: 'members', element: <MembersSettings /> },
              { path: 'roles', element: <RolesSettings /> },
              { path: 'security', element: <SecuritySettings /> },
              { path: 'ai-provider', element: <AiProviderSettings /> },
              { path: 'webhooks', element: <WebhooksSettings /> },
              { path: 'privacy', element: <PrivacySettings /> },
              { path: 'audit-log', element: <AuditLogSettings /> },
            ],
          },
          ...moduleRoutes,
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

const TOAST_ICONS = {
  success: <CheckCircle2 size={16} />,
  critical: <XCircle size={16} />,
  info: <Info size={16} />,
};

export function App() {
  return (
    <ThemeProvider>
      <ToastProvider icons={TOAST_ICONS}>
        <ConfirmProvider>
          <SessionProvider>
            <RouterProvider router={router} />
          </SessionProvider>
        </ConfirmProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
